// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { TabProps } from "@insite/mobius/Tab";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { xBaseTheme } from "@tps/XDL/types";
import color from "@tps/XDL/Utilities/color";
import { styledScrollbar } from "@tps/XDL/Utilities/scroll";
import { css } from "styled-components";

const tabSelectedStyle = css`
    outline-width: 0;
    background: transparent;
    color: ${color("primary.main")};
    ${TypographyStyle} {
        display: inline-block;
        padding-bottom: 4px;
        border-bottom: 1px solid ${color("primary.main")};
    }
    &:hover {
        color: ${color("common.ui8")};
        ${TypographyStyle} {
            border-bottom: 1px solid ${color("common.ui8")};
        }
    }
`;

const tabStyle = css`
    padding: 0;
    min-width: initial;
    user-select: none;
    position: relative;
    border: none;
    transition: all 0.2s;
    color: ${color("primary.main", 0.5)};
    max-height: 30px;
    &:hover {
        color: ${color("common.ui8")};
        cursor: pointer;
        border: none;
    }
    &:focus {
        ${tabSelectedStyle}
    }
    &:last-child:after  {
        display: none;
    }

    &:after {
        content: " ";
        box-sizing: border-box;
        width: 1px;
        line-height: 22px;
        margin: 0 20px;
        border-right: 1px solid ${color("common.ui4")};
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        typographyProps: {
            size: 16,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 22px;
            `,
        },
        css: css`
            ${tabStyle}
            ${({ selected }: TabProps) => selected && tabSelectedStyle}
        `,
    },
    groupDefaultProps: {
        mergeCss: true,
        cssOverrides: {
            tabGroup: css`
                padding: 0;
                position: relative;
                align-items: flex-start;
                ${styledScrollbar(4)}
            `,
            tabContent: css`
                margin-top: 0;
                border: none;
                background: transparent;
                box-shadow: none;
                padding: 20px 0 0;
                @media print {
                    padding: 0;
                    margin-bottom: 12px;
                    display: block;

                    & > ${TypographyStyle} {
                        display: inline-block;
                        ${tabStyle}
                        ${tabSelectedStyle}
                    }

                    & > div {
                        padding: 0;
                        border: 1px solid ${color("common.border")};
                    }
                }
            `,
            wrapper: css`
                width: 100%;
            `,
        },
    },
} as xBaseTheme["tab"];
